// src/pages/CategoryManagementPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./CategoryManagementPage.css";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaPrint,
  FaSearch,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaChevronLeft,
  FaChevronRight,
  FaRedo,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_API =
  process.env.REACT_APP_API_BASE_URL ||
  "https://sipedasapi.bkkpangkalpinang.com/api";
const ENDPOINT = `${BASE_API}/categories`;

function CategoryManagementPage({ userRole }) {
  const [categoriesTree, setCategoriesTree] = useState([]); // root categories with children
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);

  // Data form: now includes parent_id
  const [categoryData, setCategoryData] = useState({ name: "", parent_id: null });
  const [categoryType, setCategoryType] = useState("utama"); // "utama" | "sub"
  const [formErrors, setFormErrors] = useState({});

  // UI Interaktif
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [pageSize, setPageSize] = useState(5); // number of MAIN categories per page
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Expand/Collapse per parent
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());

  useEffect(() => {
    fetchCategories();
  }, []);

  // Responsive switch (table -> card list)
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);

    setIsMobile(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // ESC to close modal
  useEffect(() => {
    if (!showModal) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCloseModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, categoryData.name, categoryData.parent_id, categoryType]);

  // ---------- helpers ----------
  const normalizeToTree = (data) => {
    // If already tree (has children), just ensure children exists
    if (Array.isArray(data) && data.length > 0 && "children" in data[0]) {
      return (data || []).map((p) => ({
        ...p,
        parent_id: p.parent_id ?? null,
        children: Array.isArray(p.children) ? p.children : [],
      }));
    }

    // Else assume flat: [{id,name,parent_id}, ...] -> build tree
    const list = Array.isArray(data) ? data : [];
    const map = new Map();
    list.forEach((c) => {
      map.set(c.id, { ...c, parent_id: c.parent_id ?? null, children: [] });
    });

    const roots = [];
    map.forEach((node) => {
      if (node.parent_id && map.has(node.parent_id)) {
        map.get(node.parent_id).children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setErr("");
      // Backend index default tree; keep mode=tree explicit for safety
      const response = await axios.get(`${ENDPOINT}?mode=tree`);
      const tree = normalizeToTree(response.data);
      setCategoriesTree(tree);
    } catch (error) {
      console.error("Gagal mengambil data kategori:", error);
      setErr(error?.message || "Gagal mengambil data kategori");
      setCategoriesTree([]);
    } finally {
      setLoading(false);
    }
  };

  const mainCategories = useMemo(() => {
    return (categoriesTree || [])
      .filter((c) => c.parent_id == null)
      .map((c) => ({ id: c.id, name: c.name }));
  }, [categoriesTree]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setCategoryData((prev) => {
      if (name === "parent_id") {
        return { ...prev, parent_id: value ? Number(value) : null };
      }
      return { ...prev, [name]: value };
    });

    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTypeChange = (e) => {
    const val = e.target.value; // utama | sub
    setCategoryType(val);

    setCategoryData((prev) => {
      if (val === "utama") return { ...prev, parent_id: null };
      // sub: set default parent_id if empty
      const defaultParent = mainCategories?.[0]?.id ?? null;
      return { ...prev, parent_id: prev.parent_id ?? defaultParent };
    });

    // clear parent error if any
    if (formErrors.parent_id) setFormErrors((p) => ({ ...p, parent_id: "" }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!categoryData.name.trim()) errors.name = "Nama kategori tidak boleh kosong";
    if (categoryType === "sub" && !categoryData.parent_id)
      errors.parent_id = "Pilih kategori utama untuk sub kategori";

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      name: categoryData.name.trim(),
      parent_id: categoryType === "utama" ? null : categoryData.parent_id,
    };

    try {
      if (isEditing && currentCategory) {
        await axios.put(`${ENDPOINT}/${currentCategory.id}`, payload);
        alert("Kategori berhasil diperbarui.");
      } else {
        await axios.post(ENDPOINT, payload);
        alert("Kategori berhasil ditambahkan.");
      }

      resetFormAndCloseModal();
      await fetchCategories(); // safest: refresh tree
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        console.error("Gagal menyimpan kategori:", error);
        alert("Terjadi kesalahan saat menyimpan kategori.");
      }
    }
  };

  const handleDeleteCategory = async (id, name, hasChildren = false) => {
    // frontend guard (backend juga sudah guard)
    if (hasChildren) {
      alert("Kategori utama tidak bisa dihapus karena masih memiliki sub kategori.");
      return;
    }

    if (!window.confirm(`Yakin ingin menghapus kategori '${name}'?`)) return;

    try {
      const res = await axios.delete(`${ENDPOINT}/${id}`);
      alert(res.data?.message || `Kategori '${name}' berhasil dihapus.`);
      await fetchCategories();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Terjadi kesalahan saat menghapus kategori.";
      console.error("Gagal menghapus kategori:", error);
      alert(msg);
    }
  };

  const handleAddCategoryClick = () => {
    setIsEditing(false);
    setCurrentCategory(null);
    setCategoryType("utama");
    setCategoryData({ name: "", parent_id: null });
    setFormErrors({});
    setShowModal(true);
  };

  const handleAddSubCategoryClick = (parent) => {
    setIsEditing(false);
    setCurrentCategory(null);
    setCategoryType("sub");
    setCategoryData({ name: "", parent_id: parent?.id ?? null });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditCategoryClick = (category, parent = null) => {
    setIsEditing(true);
    setCurrentCategory(category);

    const isSub = category?.parent_id != null;
    setCategoryType(isSub ? "sub" : "utama");

    setCategoryData({
      name: category.name ?? "",
      parent_id: isSub ? (category.parent_id ?? parent?.id ?? null) : null,
    });

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (
      (categoryData.name || categoryData.parent_id) &&
      !window.confirm("Perubahan belum disimpan. Tutup?")
    )
      return;
    resetFormAndCloseModal();
  };

  const resetFormAndCloseModal = () => {
    setCategoryData({ name: "", parent_id: null });
    setCategoryType("utama");
    setFormErrors({});
    setShowModal(false);
    setIsEditing(false);
    setCurrentCategory(null);
  };

  const toggleSort = () => setSortDir((p) => (p === "asc" ? "desc" : "asc"));

  const resetFilters = () => {
    setSearch("");
    setSortDir("asc");
    setPageSize(5);
    setPage(1);
  };

  const toggleCollapseParent = (parentId) => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const expandAll = () => setCollapsedParents(new Set());
  const collapseAll = () => {
    const ids = new Set((categoriesTree || []).map((p) => p.id));
    setCollapsedParents(ids);
  };

  // =============== CETAK DAFTAR KATEGORI (TREE) ===============
  const handlePrintCategories = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup diblokir browser. Izinkan popup untuk aplikasi ini terlebih dahulu.");
      return;
    }

    const now = new Date();
    const tanggal = now.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const jam = now.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const rows = [];
    let no = 1;

    (categoriesTree || []).forEach((parent) => {
      rows.push(`
        <tr>
          <td style="border:1px solid #000; padding:4px; text-align:center;">${no++}</td>
          <td style="border:1px solid #000; padding:4px;"><b>${parent.name || ""}</b></td>
          <td style="border:1px solid #000; padding:4px; text-align:center;">Utama</td>
          <td style="border:1px solid #000; padding:4px;">-</td>
        </tr>
      `);

      (parent.children || []).forEach((child) => {
        rows.push(`
          <tr>
            <td style="border:1px solid #000; padding:4px; text-align:center;">${no++}</td>
            <td style="border:1px solid #000; padding:4px;">&nbsp;&nbsp;&nbsp;• ${child.name || ""}</td>
            <td style="border:1px solid #000; padding:4px; text-align:center;">Sub</td>
            <td style="border:1px solid #000; padding:4px;">${parent.name || ""}</td>
          </tr>
        `);
      });
    });

    const rowsHtml =
      rows.join("") ||
      `
        <tr>
          <td colspan="4" style="border:1px solid #000; padding:8px; text-align:center;">
            Tidak ada data kategori.
          </td>
        </tr>
      `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="id">
      <head>
        <meta charset="UTF-8" />
        <title>Daftar Kategori Barang</title>
        <style>
          body { font-family: "Times New Roman", serif; font-size: 11pt; margin: 15mm; }
          .kop { text-align: left; margin-bottom: 8px; }
          .kop img { max-width: 260mm; height: auto; }
          h1 { font-size: 14pt; text-align: center; margin: 0; }
          .subtitle { text-align: center; font-size: 10pt; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { border: 1px solid #000; padding: 5px; text-align: center; font-weight: bold; background: #f2f2f2; }
          td { border: 1px solid #000; padding: 4px; }
          .footer-info { margin-top: 12px; font-size: 9pt; }
          @page { size: A4 portrait; margin: 15mm 15mm 15mm 15mm; }
        </style>
      </head>
      <body>
        <div class="kop">
          <img src="/kop.png" alt="Kop Kemenkes BKK Pangkalpinang" />
        </div>

        <h1>DAFTAR KATEGORI BARANG</h1>
        <div class="subtitle">Sistem Informasi Pencatatan Data Stok (SIPEDAS)</div>

        <table>
          <thead>
            <tr>
              <th style="width:8%;">No</th>
              <th>Nama Kategori</th>
              <th style="width:12%;">Jenis</th>
              <th style="width:28%;">Kategori Utama</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>

        <div class="footer-info">Dicetak pada: ${tanggal} pukul ${jam}</div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  // ======= Filter + sort on TREE =======
  const filteredTree = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tree = categoriesTree || [];
    if (!q) return tree;

    return tree
      .map((parent) => {
        const parentMatch = String(parent?.name || "").toLowerCase().includes(q);
        const children = Array.isArray(parent.children) ? parent.children : [];
        const matchedChildren = children.filter((ch) =>
          String(ch?.name || "").toLowerCase().includes(q)
        );

        if (parentMatch) {
          // jika parent match, tampilkan semua child (biar konteksnya jelas)
          return { ...parent, children };
        }

        if (matchedChildren.length > 0) {
          // jika child match, tampilkan parent + child yang match saja
          return { ...parent, children: matchedChildren };
        }

        return null;
      })
      .filter(Boolean);
  }, [categoriesTree, search]);

  const sortedTree = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortName = (a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "id", {
        sensitivity: "base",
      }) * dir;

    const parents = [...(filteredTree || [])].sort(sortName);
    return parents.map((p) => ({
      ...p,
      children: [...(p.children || [])].sort(sortName),
    }));
  }, [filteredTree, sortDir]);

  // Pagination by MAIN categories (parents)
  const totalParents = sortedTree.length;
  const totalPages = Math.max(1, Math.ceil(totalParents / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(totalParents, startIdx + pageSize);
  const pagedParents = sortedTree.slice(startIdx, endIdx);

  const totalRowsShown = useMemo(() => {
    let count = 0;
    pagedParents.forEach((p) => {
      count += 1; // parent row
      if (!collapsedParents.has(p.id)) count += (p.children || []).length;
    });
    return count;
  }, [pagedParents, collapsedParents]);

  return (
    <div className="category-management-container">
      {/* Header */}
      <div className="category-header-row">
        <div className="title-block">
          <h2>Manajemen Kategori</h2>
          <div className="subtitle">
            Kategori Utama & Sub Kategori — responsif untuk semua layar.
          </div>
        </div>

        <div className="category-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handlePrintCategories}
            disabled={loading}
            type="button"
            title="Cetak daftar kategori"
          >
            <FaPrint /> <span>Cetak</span>
          </button>

          <button
            className="btn btn-primary"
            onClick={handleAddCategoryClick}
            disabled={loading}
            type="button"
            title="Tambah kategori utama / sub kategori"
          >
            <FaPlus /> <span>Tambah</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="search-box" role="search">
            <FaSearch className="search-icon" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Cari kategori utama / sub kategori…"
              aria-label="Cari kategori"
            />
            {search && (
              <button
                type="button"
                className="clear-search"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                aria-label="Hapus pencarian"
                title="Hapus"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={toggleSort}
            title={`Urutkan ${sortDir === "asc" ? "A-Z" : "Z-A"}`}
          >
            {sortDir === "asc" ? <FaSortAlphaDown /> : <FaSortAlphaUp />}
            <span className="hide-sm">
              {sortDir === "asc" ? "A–Z" : "Z–A"}
            </span>
          </button>

          <div className="pagesize">
            <label className="hide-sm" htmlFor="pagesize">
              Kategori Utama / halaman:
            </label>
            <select
              id="pagesize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Jumlah kategori utama per halaman"
            >
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={resetFilters}
            title="Reset filter"
          >
            <FaRedo /> <span className="hide-sm">Reset</span>
          </button>

          <button type="button" className="btn btn-ghost" onClick={expandAll} title="Buka semua">
            <FaChevronDown /> <span className="hide-sm">Expand</span>
          </button>

          <button type="button" className="btn btn-ghost" onClick={collapseAll} title="Tutup semua">
            <FaChevronUp /> <span className="hide-sm">Collapse</span>
          </button>
        </div>

        <div className="toolbar-right">
          <div className="meta-chip" title="Jumlah kategori utama">
            Utama: <b>{totalParents}</b>
          </div>
          <div className="meta-text">
            {totalParents === 0 ? (
              "Tidak ada data"
            ) : (
              <>
                Menampilkan kategori utama <b>{startIdx + 1}</b>–<b>{endIdx}</b> dari{" "}
                <b>{totalParents}</b> (baris tampil: <b>{totalRowsShown}</b>)
              </>
            )}
          </div>
        </div>
      </div>

      {/* State */}
      {loading && <div className="info-text">Memuat kategori…</div>}
      {err && !loading && <div className="error-text">Error: {err}</div>}

      {/* Content */}
      {!loading && !err && (
        <>
          {totalParents === 0 ? (
            <div className="empty-state">
              <div className="empty-title">Belum ada data kategori</div>
              <div className="empty-desc">
                Klik tombol <b>Tambah</b> untuk membuat kategori utama atau sub kategori.
              </div>
              <button className="btn btn-primary" type="button" onClick={handleAddCategoryClick}>
                <FaPlus /> Tambah Kategori
              </button>
            </div>
          ) : isMobile ? (
            // Mobile: Group cards
            <div className="card-list" role="list">
              {pagedParents.map((parent) => {
                const isCollapsed = collapsedParents.has(parent.id);
                const childCount = (parent.children || []).length;

                return (
                  <div className="parent-card" key={parent.id} role="listitem">
                    <div className="parent-card-head">
                      <button
                        type="button"
                        className="collapse-button"
                        onClick={() => toggleCollapseParent(parent.id)}
                        title={isCollapsed ? "Buka sub kategori" : "Tutup sub kategori"}
                      >
                        {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                      </button>

                      <div className="parent-card-title">
                        <div className="category-name">
                          <span className="badge badge-parent">Utama</span>
                          {parent.name}
                        </div>
                        <div className="category-subtext">{childCount} sub kategori</div>
                      </div>

                      <div className="parent-card-actions">
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => handleAddSubCategoryClick(parent)}
                          title="Tambah sub kategori"
                        >
                          <FaPlus />
                        </button>

                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => handleEditCategoryClick(parent)}
                          type="button"
                        >
                          <FaEdit /> Edit
                        </button>

                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteCategory(parent.id, parent.name, childCount > 0)}
                          type="button"
                        >
                          <FaTrash /> Hapus
                        </button>
                      </div>
                    </div>

                    {!isCollapsed && childCount > 0 && (
                      <div className="child-list">
                        {parent.children.map((child) => (
                          <div className="child-card" key={child.id}>
                            <div className="category-name child-indent">
                              <span className="badge badge-child">Sub</span>
                              {child.name}
                            </div>

                            <div className="child-card-actions">
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={() => handleEditCategoryClick(child, parent)}
                                type="button"
                              >
                                <FaEdit /> Edit
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteCategory(child.id, child.name)}
                                type="button"
                              >
                                <FaTrash /> Hapus
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop/Tablet: Group table
            <div className="table-responsive" aria-label="Daftar kategori">
              <table className="categories-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}></th>
                    <th>Nama Kategori</th>
                    <th style={{ width: 200 }}>Jenis</th>
                    <th style={{ width: 260 }}>Kategori Utama</th>
                    <th style={{ width: 320 }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedParents.map((parent) => {
                    const isCollapsed = collapsedParents.has(parent.id);
                    const childCount = (parent.children || []).length;

                    return (
                      <React.Fragment key={parent.id}>
                        <tr className="row-parent">
                          <td>
                            <button
                              type="button"
                              className="collapse-button"
                              onClick={() => toggleCollapseParent(parent.id)}
                              title={isCollapsed ? "Buka sub kategori" : "Tutup sub kategori"}
                            >
                              {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                            </button>
                          </td>

                          <td className="name-cell">
                            <span className="badge badge-parent">Utama</span>
                            <span className="name-strong">{parent.name}</span>
                            <span className="count-pill">{childCount} sub</span>
                          </td>

                          <td>Категori Utama</td>
                          <td>-</td>

                          <td className="category-actions">
                            <button
                              className="btn btn-ghost btn-sm"
                              type="button"
                              onClick={() => handleAddSubCategoryClick(parent)}
                              title="Tambah sub kategori"
                            >
                              <FaPlus /> Sub
                            </button>

                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleEditCategoryClick(parent)}
                              type="button"
                            >
                              <FaEdit /> Edit
                            </button>

                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteCategory(parent.id, parent.name, childCount > 0)}
                              type="button"
                              title={childCount > 0 ? "Hapus sub kategori dulu" : "Hapus kategori utama"}
                            >
                              <FaTrash /> Hapus
                            </button>
                          </td>
                        </tr>

                        {!isCollapsed &&
                          (parent.children || []).map((child) => (
                            <tr className="row-child" key={child.id}>
                              <td></td>
                              <td className="name-cell">
                                <span className="child-indent">
                                  <span className="badge badge-child">Sub</span>
                                  {child.name}
                                </span>
                              </td>
                              <td>Sub Kategori</td>
                              <td>{parent.name}</td>
                              <td className="category-actions">
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => handleEditCategoryClick(child, parent)}
                                  type="button"
                                >
                                  <FaEdit /> Edit
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDeleteCategory(child.id, child.name)}
                                  type="button"
                                >
                                  <FaTrash /> Hapus
                                </button>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                title="Sebelumnya"
              >
                <FaChevronLeft /> <span className="hide-sm">Prev</span>
              </button>

              <div className="pagination-info">
                Halaman <b>{page}</b> / <b>{totalPages}</b>
              </div>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                title="Berikutnya"
              >
                <span className="hide-sm">Next</span> <FaChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={isEditing ? "Edit kategori" : "Tambah kategori"}
          onMouseDown={(e) => {
            if (e.target.classList.contains("modal-overlay")) handleCloseModal();
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3>{isEditing ? "Edit Kategori" : "Tambah Kategori"}</h3>
                <div className="modal-subtitle">
                  Pilih jenis kategori, isi nama, lalu simpan.
                </div>
              </div>

              <button
                className="icon-button"
                onClick={handleCloseModal}
                type="button"
                aria-label="Tutup"
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <form className="category-form" onSubmit={handleFormSubmit}>
                {/* Jenis kategori */}
                <div className="form-group">
                  <label htmlFor="categoryType">Jenis Kategori</label>
                  <select
                    id="categoryType"
                    value={categoryType}
                    onChange={handleTypeChange}
                  >
                    <option value="utama">Kategori Utama</option>
                    <option value="sub">Sub Kategori</option>
                  </select>
                </div>

                {/* Parent select (only if sub) */}
                {categoryType === "sub" && (
                  <div className="form-group">
                    <label htmlFor="parentSelect">Kategori Utama</label>
                    <select
                      id="parentSelect"
                      name="parent_id"
                      value={categoryData.parent_id ?? ""}
                      onChange={handleInputChange}
                      className={formErrors.parent_id ? "input-error" : ""}
                    >
                      <option value="">-- pilih kategori utama --</option>
                      {mainCategories.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.parent_id && (
                      <span className="error-message">{formErrors.parent_id}</span>
                    )}
                  </div>
                )}

                {/* Nama */}
                <div className="form-group">
                  <label htmlFor="categoryName">Nama Kategori</label>
                  <input
                    type="text"
                    id="categoryName"
                    name="name"
                    value={categoryData.name}
                    onChange={handleInputChange}
                    className={formErrors.name ? "input-error" : ""}
                    placeholder={categoryType === "utama" ? "cth: OBAT" : "cth: Tablet / Serbuk"}
                    autoFocus
                  />
                  {formErrors.name && (
                    <span className="error-message">{formErrors.name}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {isEditing ? "Simpan Perubahan" : "Tambah Kategori"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Floating add button (mobile only) */}
      <button
        className="fab"
        type="button"
        onClick={handleAddCategoryClick}
        title="Tambah kategori"
        aria-label="Tambah kategori"
      >
        <FaPlus />
      </button>
    </div>
  );
}

export default CategoryManagementPage;